import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Search, Code2, Database, Globe, X, Command } from 'lucide-react';

const METHOD_COLORS = {
  GET: '#30d986', POST: '#4f8ef7', PUT: '#f5a623',
  PATCH: '#9d6ef5', DELETE: '#f05c5c', HEAD: '#8892a4', OPTIONS: '#8892a4'
};

export default function GlobalSearch({ projectId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ endpoints: [], schemas: [], environments: [] });
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);
  const debounce = useRef(null);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) { setTimeout(() => inputRef.current?.focus(), 50); setQuery(''); setResults({ endpoints: [], schemas: [], environments: [] }); }
  }, [open]);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults({ endpoints: [], schemas: [], environments: [] }); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/projects/${projectId}/search?q=${encodeURIComponent(query)}`);
        setResults(res.data);
        setHighlighted(0);
      } catch {} finally { setLoading(false); }
    }, 250);
  }, [query, projectId]);

  const allResults = [
    ...results.endpoints.map(e => ({ ...e, _type: 'endpoint' })),
    ...results.schemas.map(s => ({ ...s, _type: 'schema' })),
    ...results.environments.map(v => ({ ...v, _type: 'env' })),
  ];

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, allResults.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter' && allResults[highlighted]) selectResult(allResults[highlighted]);
  }

  function selectResult(item) {
    setOpen(false);
    onSelect && onSelect(item);
  }

  function highlight(text) {
    if (!query) return text;
    const idx = text?.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1 || idx === undefined) return text;
    return (
      <>{text.substring(0, idx)}<mark style={sty.mark}>{text.substring(idx, idx + query.length)}</mark>{text.substring(idx + query.length)}</>
    );
  }

  if (!open) return (
    <button style={sty.trigger} onClick={() => setOpen(true)} title="Search (Ctrl+K)">
      <Search size={14} />
      <span style={sty.triggerText}>Search APIs...</span>
      <span style={sty.kbd}><Command size={10} />K</span>
    </button>
  );

  return (
    <div style={sty.overlay} onClick={() => setOpen(false)}>
      <div style={sty.modal} onClick={e => e.stopPropagation()}>
        <div style={sty.inputRow}>
          <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            style={sty.input}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search endpoints, schemas, variables..."
          />
          {loading && <div style={sty.spinner} />}
          <button style={sty.closeBtn} onClick={() => setOpen(false)}><X size={14} /></button>
        </div>

        {allResults.length > 0 && (
          <div style={sty.results}>
            {results.endpoints.length > 0 && (
              <div style={sty.group}>
                <div style={sty.groupLabel}>Endpoints</div>
                {results.endpoints.map((ep, i) => {
                  const idx = allResults.findIndex(r => r._type === 'endpoint' && r.id === ep.id);
                  return (
                    <div key={ep.id} style={{ ...sty.row, ...(highlighted === idx ? sty.rowActive : {}) }}
                      onClick={() => selectResult({ ...ep, _type: 'endpoint' })}
                      onMouseEnter={() => setHighlighted(idx)}>
                      <span style={{ ...sty.method, color: METHOD_COLORS[ep.method] }}>{ep.method}</span>
                      <div style={sty.rowInfo}>
                        <span style={sty.rowName}>{highlight(ep.name)}</span>
                        <span style={sty.rowSub} className="mono">{highlight(ep.path)}</span>
                      </div>
                      <span style={sty.statusChip}>{ep.status}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {results.schemas.length > 0 && (
              <div style={sty.group}>
                <div style={sty.groupLabel}>Schemas</div>
                {results.schemas.map((s) => {
                  const idx = allResults.findIndex(r => r._type === 'schema' && r.id === s.id);
                  return (
                    <div key={s.id} style={{ ...sty.row, ...(highlighted === idx ? sty.rowActive : {}) }}
                      onClick={() => selectResult({ ...s, _type: 'schema' })}
                      onMouseEnter={() => setHighlighted(idx)}>
                      <Database size={14} style={{ color: 'var(--accent-purple)', flexShrink: 0 }} />
                      <div style={sty.rowInfo}>
                        <span style={sty.rowName}>{highlight(s.name)}</span>
                        {s.description && <span style={sty.rowSub}>{s.description}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {query.length >= 2 && !loading && allResults.length === 0 && (
          <div style={sty.empty}>No results for "<strong>{query}</strong>"</div>
        )}

        <div style={sty.footer}>
          <span style={sty.hint}><kbd style={sty.kbdSmall}>↑↓</kbd> navigate</span>
          <span style={sty.hint}><kbd style={sty.kbdSmall}>↵</kbd> select</span>
          <span style={sty.hint}><kbd style={sty.kbdSmall}>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

const sty = {
  trigger: { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 7, padding: '5px 12px', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--sans)', minWidth: 180 },
  triggerText: { flex: 1, textAlign: 'left' },
  kbd: { display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 4px', color: 'var(--text-muted)' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14vh', zIndex: 2000, backdropFilter: 'blur(4px)' },
  modal: { background: 'var(--bg-secondary)', border: '1px solid var(--border-light)', borderRadius: 12, width: 600, maxWidth: '95vw', boxShadow: '0 32px 100px rgba(0,0,0,0.6)', overflow: 'hidden' },
  inputRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)' },
  input: { flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 15, padding: 0 },
  spinner: { width: 16, height: 16, border: '2px solid var(--border)', borderTop: '2px solid var(--accent)', borderRadius: '50%', animation: 'spin 0.6s linear infinite', flexShrink: 0 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 3, display: 'flex', borderRadius: 4 },
  results: { maxHeight: 360, overflow: 'auto' },
  group: { padding: '8px 0' },
  groupLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', padding: '4px 16px 6px' },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer', transition: 'background 0.1s' },
  rowActive: { background: 'var(--accent-dim)' },
  method: { fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 10, width: 50, flexShrink: 0 },
  rowInfo: { flex: 1, overflow: 'hidden' },
  rowName: { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowSub: { display: 'block', fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  statusChip: { fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: 4, flexShrink: 0 },
  empty: { padding: '20px 16px', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' },
  footer: { display: 'flex', gap: 16, padding: '8px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-primary)' },
  hint: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)' },
  kbdSmall: { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 3, padding: '1px 5px', fontSize: 10, color: 'var(--text-secondary)', fontFamily: 'var(--mono)' },
  mark: { background: 'rgba(79,142,247,0.3)', color: 'var(--accent)', borderRadius: 2 },
};
